<script setup lang="ts">
import { computed, ref } from "vue";

type Props = {
  pageSize: number;
};

const { pageSize } = defineProps<Props>();

const totalItems = ref(0);
const slider = useTemplateRef("slider");

const { x, arrivedState } = useScroll(slider, {
  behavior: "smooth",
});
const { hasOverflow } = useOverflow(slider);
const { width } = useElementSize(slider);

const itemWidth = computed(() => width.value / pageSize);

const goToItem = (itemIndex: number) => {
  x.value = itemIndex * itemWidth.value;
};

onBeforeMount(() => {
  const slots = useSlots();
  totalItems.value = (slots.default?.()[0]?.children?.length as number) ?? 0;
});

onMounted(() => {
  x.value = 0;

  // The +1 here is to make sure that the slider actually reaches or exceeds the rightmost point.
  arrivedState.right =
    Math.abs(slider.value?.scrollLeft || 0) +
      (slider.value?.clientWidth || 0) >=
    (slider.value?.scrollWidth || 0) + 1;
});

const currentSlideIndex = computed(() =>
  Math.ceil((Math.max(x.value, 1) + 1) / itemWidth.value),
);

const canSlideNext = computed(() => {
  // when the page first loads on a new browser, the width of the dom element is 0,
  // so the arrivedState.right is always true, to avoid disabling the button,
  // we need to exclude the above situation by checking if the the hasOverflow is true and both arrivedState.right and arrivedState.left are true
  if (hasOverflow.value && arrivedState.right && arrivedState.left) return true;

  return !arrivedState.right && hasOverflow.value;
});
const canSlidePrev = computed(() => !arrivedState.left);

const nextPage = () => {
  x.value += itemWidth.value * pageSize;
};

const prevPage = () => {
  x.value -= itemWidth.value * pageSize;
};

const nextSlide = () => {
  x.value += itemWidth.value;
};

const prevSlide = () => {
  x.value -= itemWidth.value;
};

defineExpose({
  nextPage,
  prevPage,
  nextSlide,
  prevSlide,
  canSlideNext,
  canSlidePrev,
  totalItems,
  currentSlideIndex,
  goToItem,
});
</script>

<template>
  <div class="media-gallery">
    <div
      class="slider-container"
      data-test="slider"
    >
      <ul
        ref="slider"
        class="slider overflow-x-scroll gap-x-1"
      >
        <slot />
      </ul>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.media-gallery {
  overflow: hidden;
  width: 100%;
}

.slider-container {
  position: relative;
  width: 100%;
}

.slider {
  @apply flex min-w-full w-full;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }
}

.slider > * {
  scroll-snap-align: start;
}
</style>
